import React, { useMemo } from "react";
import Highcharts from "highcharts";
import { format } from "date-fns";
import { renderToStaticMarkup } from "react-dom/server";
import { Theme } from "@mui/material";
import { ChartTooltip } from "./ChartTooltip";

interface ChartDataPoint {
  date: Date;
  value: number;
}

interface UseChartOptionsProps {
  data: ChartDataPoint[];
  theme: Theme;
  chartColors: {
    mainLine: string;
    mainArea: string;
  };
  tickFormat: string;
  tickInterval: number;
  onPointClick: (value: number, date: Date) => void;
}

export const useChartOptions = ({
  data,
  theme,
  chartColors,
  tickFormat,
  tickInterval,
  onPointClick,
}: UseChartOptionsProps): Highcharts.Options => {
  return useMemo(() => {
    const seriesData: [number, number][] = data.map((item) => [
      item.date.getTime(),
      item.value,
    ]);

    const axisLabelStyle: Highcharts.CSSObject = {
      color: theme.palette.text.secondary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body3.fontSize as string,
      fontWeight: String(theme.typography.body3.fontWeight),
    };

    return {
      chart: {
        type: "areaspline",
        height: 300,
        backgroundColor: "transparent",
        spacing: [20, 20, 24, 20],
      },
      title: undefined,
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        type: "datetime",
        lineColor: theme.palette.border.secondary,
        tickLength: 0,
        ...(tickInterval > 0 && {
          tickInterval: tickInterval * 24 * 60 * 60 * 1000,
        }),
        labels: {
          style: axisLabelStyle,
          formatter: function () {
            return format(new Date(this.value as number), tickFormat);
          },
        },
      },
      yAxis: {
        title: undefined,
        gridLineColor: theme.palette.border.secondary,
        labels: {
          style: axisLabelStyle,
        },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { zIndex: 4001 },
        formatter: function () {
          const x = this.x as number;
          const y = this.y as number;

          return renderToStaticMarkup(
            <ChartTooltip
              value={y}
              date={new Date(x)}
              color={chartColors.mainLine}
              theme={theme}
            />
          );
        },
      },
      plotOptions: {
        areaspline: {
          lineColor: chartColors.mainLine,
          lineWidth: 2,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 5,
              },
            },
          },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0.4, chartColors.mainArea],
              [
                1,
                Highcharts.color(chartColors.mainArea)
                  .setOpacity(0)
                  .get("rgba") as string,
              ],
            ],
          },
          threshold: null,
        },
        series: {
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                const x = this.x as number;
                const y = this.y as number;
                onPointClick(y, new Date(x));
              },
            },
          },
        },
      },
      series: [
        {
          type: "areaspline",
          data: seriesData,
        },
      ],
    };
  }, [data, theme, chartColors, tickFormat, tickInterval, onPointClick]);
};
